/**
 * OrgAdminGate · route-level gate by ORG admin status.
 *
 * Sibling to RoleGate (gates by logistics role) and PhaseGate (gates by feature
 * flag). Use this for surfaces that only the organization owner / admin should
 * reach — e.g. "Claim your CAB #", which manages a company-wide credential and
 * should not be reachable by an invited team member.
 *
 *   <OrgAdminGate><ClaimCABPage /></OrgAdminGate>
 *
 * Platform (Cipher & Row staff) admins bypass via useOrgRole (isOrgAdmin already
 * includes them) so Jack/Louis can preview. While the role resolves we render
 * nothing to avoid a flash or a wrong redirect. On deny: redirect + toast.
 */
import { ReactNode, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useOrgRole } from "@/hooks/useOrgRole";
import { toast } from "sonner";

interface OrgAdminGateProps {
  /** Where to send blocked members. Defaults to "/dash". */
  redirectTo?: string;
  /** Toast message on deny. */
  denyMessage?: string;
  children: ReactNode;
}

export function OrgAdminGate({
  redirectTo = "/dash",
  denyMessage,
  children,
}: OrgAdminGateProps) {
  const { isOrgAdmin, loading } = useOrgRole();
  const location = useLocation();
  const toastedRef = useRef(false);

  // While resolving, render nothing so we never flash the page to a member or
  // bounce an admin before we know who they are.
  if (loading) return null;
  if (isOrgAdmin) return <>{children}</>;

  return (
    <OrgAdminGateDeny
      redirectTo={redirectTo}
      denyMessage={denyMessage}
      from={location.pathname}
      toastedRef={toastedRef}
    />
  );
}

function OrgAdminGateDeny({
  redirectTo,
  denyMessage,
  from,
  toastedRef,
}: {
  redirectTo: string;
  denyMessage?: string;
  from: string;
  toastedRef: React.MutableRefObject<boolean>;
}) {
  useEffect(() => {
    if (toastedRef.current) return;
    toastedRef.current = true;
    toast.message(
      denyMessage ?? "That page is managed by your organization's admin.",
    );
  }, [denyMessage, toastedRef]);

  return <Navigate to={redirectTo} replace state={{ from }} />;
}

export default OrgAdminGate;
